import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isCompanyAdmin } from "../../helpers/userAccess";
import { SocketContext } from "../../context/Socket/SocketContext";
import { BORDERS } from "../../theme/redesign";

/*
 * REVISIÓN 2026-09 — HECHO en esta pasada:
 *   #1  socket.off ahora remueve solo los handlers propios (helper `on()` + `listeners`).
 *   #2  quitado el setTimeout impuro del reducer UPDATE_TICKET_PRESENCE.
 *   #5  el path action:"create" del handler `company-*-ticket` ahora deduplica con
 *       `ticketsListRef.current` (antes usaba `ticketsList`, closure congelada a []).
 *   #9  el efecto RESET ahora depende de `sortAscending` y `searchOnMessages`.
 *   #10 el handler `appMessage` action:"update" solo despacha UPDATE_TICKET si el ticket
 *       ya está en la lista o pertenece a esta pestaña (antes el reducer hacía unshift ciego).
 *   #11 eliminado el setInterval de 2s que reordenaba toda la lista (era básicamente un
 *       no-op de orden: su dispatch LOAD_TICKETS actualiza en su lugar por id, no reordena).
 *       El orden DESC lo garantizan ADD_TICKET / UPDATE_TICKET / UPDATE_TICKET_UNREAD_MESSAGES,
 *       que ya ordenaban; LOAD_TICKETS conserva su semántica previa (orden del backend).
 *   - reducers ahora inmutables: nada de state[i]= / push / unshift / splice sobre `state`.
 *   - eliminados los 3 efectos duplicados literalmente (cleanup unmount, updateCount, intervalo).
 *   - lógica "¿el ticket pertenece a esta pestaña?" unificada en `ticketBelongsToTab(ticket, status)`
 *     (antes reimplementada en shouldUpdateTicket, bloque create, bloque update y efecto de limpieza,
 *     con la definición de "IA" divergiendo entre sitios; ahora todos usan isAIAgent()).
 *
 * PENDIENTE (fuera del alcance de esta pasada):
 *   #17 triple filtrado (backend + efecto de "defensa en profundidad" + efecto de 6 reglas O(n)).
 *       El efecto de limpieza y el de carga siguen filtrando en cliente; consolidarlos requiere
 *       confiar plenamente en el filtrado del backend por pestaña.
 *   - orden ASC (`sortAscending=true`): el backend lo respeta en la carga, pero las
 *     actualizaciones en vivo siempre reordenan DESC en el reducer.
 */

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: 0, // Importante para flexbox
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderTop: BORDERS.base(theme),
    minHeight: 0,
    padding: "8px 8px 12px",
    backgroundColor: theme.mode === "dark" ? "#111827" : "#ffffff",
    [theme.breakpoints.down("sm")]: {
      padding: "0 0 80px",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },

  noTicketsText: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
    color: theme.palette.text.primary,
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

// Orden estándar de la lista: updatedAt descendente (más reciente primero).
// NOTA: el prop `sortAscending` sí lo respeta el backend en la carga inicial,
// pero las actualizaciones en vivo se reordenan siempre DESC aquí. Unificar
// dirección de orden con el backend es un pendiente aparte de esta revisión.
const byUpdatedAtDesc = (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt);

// Clona el ticket dándole nuevas referencias a los sub-objetos que la UI observa
// para detectar cambios (tags/contact/user). Evita mutar el objeto entrante.
const withFreshRefs = (ticket) => ({
  ...ticket,
  tags: Array.isArray(ticket.tags)
    ? ticket.tags.map((tag) => ({ ...tag }))
    : (ticket.tags || []),
  contact: ticket.contact ? { ...ticket.contact } : ticket.contact,
  user: ticket.user ? { ...ticket.user } : ticket.user,
});

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    // Merge inmutable por id, misma semántica que antes: los existentes se
    // reemplazan en su lugar (y saltan al frente si traen no leídos), los nuevos
    // se agregan al final respetando el orden del backend. Sin mutar `state`.
    let next = [...state];
    newTickets.forEach((ticket) => {
      const ticketIndex = next.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        next[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          next = [ticket, ...next.slice(0, ticketIndex), ...next.slice(ticketIndex + 1)];
        }
      } else {
        next.push(ticket);
      }
    });

    return next;
  }

  if (action.type === "ADD_TICKET") {
    const ticket = action.payload;

    // ✅ VERIFICAR SI EL TICKET YA EXISTE PARA EVITAR DUPLICADOS
    const existingTicketIndex = state.findIndex((t) => t.id === ticket.id);
    if (existingTicketIndex !== -1) {
      // ✅ SI YA EXISTE, ACTUALIZAR EN LUGAR DE DUPLICAR
      const next = [...state];
      next[existingTicketIndex] = withFreshRefs(ticket);
      return next;
    }

    // ✅ SI NO EXISTE, AGREGAR Y REORDENAR (antes lo hacía el setInterval de 2s)
    return [withFreshRefs(ticket), ...state].sort(byUpdatedAtDesc);
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex === -1) return state;

    const next = [...state];
    next[ticketIndex] = { ...next[ticketIndex], unreadMessages: 0 };
    return next;
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    const next = [...state];

    if (ticketIndex !== -1) {
      // ✅ CRÍTICO: Usar updatedAt del backend si viene, sino fecha actual.
      const ticketUpdatedAt = ticket.updatedAt
        ? new Date(ticket.updatedAt).toISOString()
        : new Date().toISOString();

      next[ticketIndex] = {
        ...withFreshRefs(ticket),
        lastMessage: ticket.lastMessage
          ? (typeof ticket.lastMessage === "string" ? ticket.lastMessage : { ...ticket.lastMessage })
          : ticket.lastMessage,
        updatedAt: ticketUpdatedAt,
      };
    } else {
      next.unshift(withFreshRefs(ticket));
    }

    // ✅ REORDENAR POR updatedAt EN ORDEN DESCENDENTE
    return next.sort(byUpdatedAtDesc);
  }

  // ✅ AGREGADO: Actualizar solo las reacciones del ticket
  if (action.type === "UPDATE_TICKET_REACTIONS") {
    const { ticketId, lastMessageWithReactions } = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex === -1) return state;

    const next = [...state];
    next[ticketIndex] = {
      ...next[ticketIndex],
      lastMessageWithReactions,
    };
    return next;
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    const next = ticketIndex !== -1
      ? state.filter((_, i) => i !== ticketIndex)
      : [...state];

    next.unshift(ticket);

    // ✅ REORDENAR POR updatedAt EN ORDEN DESCENDENTE
    return next.sort(byUpdatedAtDesc);
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    // ✅ FIX 05/12/2025: Buscar por contactId o contact.id como fallback
    const ticketIndex = state.findIndex((t) =>
      t.contactId === contact.id || t.contact?.id === contact.id
    );
    if (ticketIndex === -1) return state;

    const next = [...state];
    next[ticketIndex] = {
      ...next[ticketIndex],
      contact: { ...contact },
    };
    return next;
  }

  if (action.type === "UPDATE_TICKET_PRESENCE") {
    const data = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === data.ticketId);
    if (ticketIndex === -1) return state;
    // Actualización inmutable: nuevo objeto de ticket + nuevo array.
    // El auto-ocultado de "composing" a los 10 s lo gestiona el handler de socket
    // (presenceTimeouts ref + dispatch real), NUNCA el reducer: un setTimeout aquí
    // era impuro y su return se descartaba (no despachaba nada).
    const next = [...state];
    next[ticketIndex] = { ...next[ticketIndex], presence: data.presence };
    return next;
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    return state.filter((t) => t.id !== ticketId);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = (props) => {
  // ✅ TODOS LOS HOOKS DEBEN IR AL INICIO - SIN EXCEPCIONES
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    aiAgentFilter, // ✅ NUEVO PROP PARA FILTRAR AGENTES IA
    excludeAIAgents, // ✅ NUEVO PROP PARA EXCLUIR AGENTES IA
    searchOnMessages, // ✅ NUEVO PROP PARA BUSCAR EN MENSAJES
    withUnreadMessages,
    isActiveTab = true, // ✅ NUEVO PROP PARA INDICAR SI ES LA PESTAÑA ACTIVA
    sortAscending = false, // ✅ NUEVO PROP PARA ORDENAMIENTO
    channel = "all",
  } = props || {};
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  // Mantener una referencia siempre actualizada de la lista para usarla en callbacks de socket
  const ticketsListRef = useRef([]);
  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user || {};
  const isMounted = useRef(true);
  const presenceTimeouts = useRef({});
  const socketManager = useContext(SocketContext);
  
  // ✅ MOVER TODOS LOS HOOKS AL INICIO - ANTES DE CUALQUIER VERIFICACIÓN CONDICIONAL
  // ✅ Normalizar selectedQueueIds para evitar reseteos innecesarios
  const normalizedQueueIds = selectedQueueIds && selectedQueueIds.length > 0 ? selectedQueueIds : null;
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    // #9: incluidos sortAscending y searchOnMessages para que al cambiarlos se
    // recargue la lista desde cero (useTickets ya refetch-ea con esos params).
  }, [status, searchParam, dispatch, showAll, tags, users, normalizedQueueIds, aiAgentFilter, excludeAIAgents, sortAscending, searchOnMessages, withUnreadMessages, channel]);


  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam: isActiveTab ? searchParam : "", // ✅ Solo buscar si es la pestaña activa
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: selectedQueueIds && selectedQueueIds.length > 0 ? JSON.stringify(selectedQueueIds) : undefined, // ✅ Solo pasar queueIds si hay filtros activos
    searchOnMessages: searchOnMessages,
    withUnreadMessages,
    sortAscending: sortAscending, // ✅ AGREGADO: Parámetro de ordenamiento
    channel,
  });

  useEffect(() => {
    const queueIds = queues?.map((q) => q.id) || [];
    
    // ✅ OPTIMIZADO: Logs solo en desarrollo o cuando sea necesario
    // console.log(`[TicketsListCustom] 📥 Cargando tickets desde backend para pestaña "${status}":`, tickets.length);
    
    // ✅ CORRECCIÓN 2025-12-23: Aplicar filtros de seguridad en frontend como capa adicional
    // El backend ya filtra correctamente, pero esto es defensa en profundidad
    let filteredTickets = tickets;
    
    // ✅ Si el usuario NO es admin, SIEMPRE filtrar por departamento (incluso si showAll es true)
    if (!isCompanyAdmin(user)) {
      if (queueIds.length > 0) {
        // ✅ Filtrar por departamentos asignados
        filteredTickets = tickets.filter(
          (t) => {
            // ✅ Si el ticket tiene departamento, debe estar en los departamentos del usuario
            if (t.queueId) {
              return queueIds.indexOf(t.queueId) > -1;
            }
            // ✅ Si el ticket NO tiene departamento, solo mostrarlo si allTicket = enabled
            const showTicketWithoutQueue = user?.allTicket === 'enabled';
            return showTicketWithoutQueue;
          }
        );
      } else {
        // ✅ Si el usuario no tiene departamentos asignados, solo mostrar tickets sin departamento si allTicket = enabled
        const showTicketWithoutQueue = user?.allTicket === 'enabled';
        if (showTicketWithoutQueue) {
          filteredTickets = tickets.filter((t) => !t.queueId);
        } else {
          filteredTickets = []; // ✅ No mostrar tickets si no tiene departamentos y allTicket = disabled
        }
      }
    } else {
      // ✅ ADMIN: Puede ver todos los tickets sin filtrar
      filteredTickets = tickets;
    }

    // ✅ APLICAR FILTRO DE AGENTES IA SI ES NECESARIO
    if (aiAgentFilter) {
      filteredTickets = filteredTickets.filter(ticket => isAIAgent(ticket));
    }
    
    // ✅ EXCLUIR AGENTES IA SI ES NECESARIO
    if (excludeAIAgents) {
      filteredTickets = filteredTickets.filter(ticket => !isAIAgent(ticket));
    }

    if (channel && channel !== "all") {
      filteredTickets = filteredTickets.filter((ticket) => {
        const raw = String(ticket?.channel || ticket?.whatsapp?.type || "whatsapp").toLowerCase();
        const ticketChannel = raw === "telegram" ? "telegram" : "whatsapp";
        return ticketChannel === channel;
      });
    }
    
    // ✅ CORRECCIÓN 2025-12-23: Solo admins pueden ver todos los tickets sin filtrar
    // ✅ USERS siempre ven solo sus departamentos asignados (defensa en profundidad)
    const ticketsToDispatch = filteredTickets;
    
    // ✅ OPTIMIZADO: Logs solo en desarrollo
    // console.log(`[TicketsListCustom] 📤 Despachando ${ticketsToDispatch.length} tickets para pestaña "${status}"`);
    
    dispatch({ type: "LOAD_TICKETS", payload: ticketsToDispatch });
  }, [tickets, status, searchParam, queues, profile, aiAgentFilter, excludeAIAgents, showAll, user, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ NUEVO: LIMPIEZA AUTOMÁTICA DE TICKETS INCORRECTOS
  useEffect(() => {
    if (!ticketsList || ticketsList.length === 0) return;
    
    // ✅ CORREGIDO: No limpiar tickets cuando status === "all"
    if (status === "all") {
      return; // ✅ No eliminar tickets en la vista "all"
    }
    
    // Un ticket sobra en esta pestaña si no le pertenece según ticketBelongsToTab.
    const ticketsToRemove = ticketsList
      .filter((ticket) => !ticketBelongsToTab(ticket, status))
      .map((ticket) => ticket.id);

    if (ticketsToRemove.length > 0) {
      ticketsToRemove.forEach((ticketId) => {
        dispatch({ type: "DELETE_TICKET", payload: ticketId });
      });
    }
  }, [ticketsList, status]); // eslint-disable-line react-hooks/exhaustive-deps


  // Cambio hecho desde esta misma pestaña (p. ej. aceptar): reflejarlo sin esperar el socket.
  useEffect(() => {
    const onLocalTicket = (e) => {
      const updated = e?.detail;
      if (!updated?.id) return;
      if (status === "all" || ticketBelongsToTab(updated, status)) {
        dispatch({ type: "UPDATE_TICKET", payload: updated });
      } else {
        dispatch({ type: "DELETE_TICKET", payload: updated.id });
      }
    };
    window.addEventListener("watoolx:ticket-local-update", onLocalTicket);
    return () => window.removeEventListener("watoolx:ticket-local-update", onLocalTicket);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ CLEANUP DEL COMPONENTE
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // ✅ LIMPIAR TODOS LOS TIMEOUTS AL DESMONTAR
      Object.values(presenceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      presenceTimeouts.current = {};
    };
  }, []);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  // #11: eliminado el setInterval de 2s que reordenaba la lista. Era básicamente
  // un no-op de orden (LOAD_TICKETS actualiza en su lugar por id, no reordena) y
  // sólo generaba re-renders. El orden lo garantiza ahora el reducer.

  // ✅ FUNCIÓN PARA IDENTIFICAR AGENTES IA - LÓGICA CORREGIDA (FASE 1)
  // ✅ CORRECCIÓN 30/12/2025: Incluir TODOS los tipos de automatización
  // ✅ MODELO DE NEGOCIO APROBADO: promptId != null OR chatbot = true OR integrationId != null
  // ✅ FUNCIÓN PARA IDENTIFICAR AGENTES IA - INCLUYE FLOWBUILDER
  const isAIAgent = (ticket) => {
    if (!ticket) return false;
    
    // ✅ CRITERIOS PARA DETECTAR TICKET CON AUTOMATIZACIÓN:
    // 1. promptId != null → IA real (OpenAI, OpenRouter)
    // 2. chatbot = true → Chatbot de menú
    // 3. integrationId != null → Integraciones externas (Typebot, n8n)
    // 4. flowStatus != null → FlowBuilder (canal independiente)
    return (
      ticket.promptId != null ||
      ticket.chatbot === true ||
      ticket.integrationId != null ||
      ticket.flowStatus != null
    );
  };

  // Única definición de "¿este ticket pertenece a la pestaña `tabStatus`?".
  // Antes esta lógica estaba duplicada (con matices distintos) en shouldUpdateTicket,
  // el bloque create, el bloque update y el efecto de limpieza.
  const ticketChannelOf = (ticket) => {
    const raw = String(ticket?.channel || ticket?.whatsapp?.type || "whatsapp").toLowerCase();
    return raw === "telegram" ? "telegram" : "whatsapp";
  };

  const ticketBelongsToTab = (ticket, tabStatus) => {
    if (!ticket) return false;
    if (channel && channel !== "all" && ticketChannelOf(ticket) !== channel) {
      return false;
    }
    const isGroupTicket = !!(ticket.contact && ticket.contact.isGroup);
    if (isGroupTicket && ticketChannelOf(ticket) === "telegram" && !user?.allowTelegramGroups) {
      return false;
    }
    if (isGroupTicket && ticketChannelOf(ticket) !== "telegram" && !user?.allowGroup && tabStatus !== "group") {
      return false;
    }
    const ticketHasAI = isAIAgent(ticket);
    const ticketStatus = ticket.status;

    if (tabStatus === "ai") return ticketHasAI;
    if (tabStatus === "group") {
      if (ticketChannelOf(ticket) === "telegram" && !user?.allowTelegramGroups) return false;
      if (ticketChannelOf(ticket) !== "telegram" && !user?.allowGroup) return false;
      return isGroupTicket && (ticketStatus === "open" || ticketStatus === "pending");
    }
    if (tabStatus === "closed") return ticketStatus === "closed";
    if (tabStatus === "all") {
      return (ticketStatus === "open" || ticketStatus === "pending") && !isGroupTicket;
    }
    // Pestañas normales (open / pending): sin IA, sin grupos, status coincidente.
    return !ticketHasAI && !isGroupTicket && ticketStatus === tabStatus;
  };

  useEffect(() => {
    isMounted.current = true;
    
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // El socket es COMPARTIDO entre componentes (TicketsManagerTabs, Ticket,
    // MessagesList, etc.). Registramos con handler nombrado y en el cleanup
    // removemos SOLO estos handlers — nunca `socket.off("evento")` a secas, que
    // borraría los listeners de los demás componentes (bug #1 de la revisión).
    const listeners = [];
    const on = (event, handler) => {
      socket.on(event, handler);
      listeners.push([event, handler]);
    };

    const shouldUpdateTicket = (ticket) => {
      // ✅ 1. VERIFICAR PERMISOS DE USUARIO
      // ✅ CORRECCIÓN: Comparar números como números, no como strings
      const ticketUserIdNum = ticket.userId ? Number(ticket.userId) : null;
      const currentUserIdNum = user?.id ? Number(user?.id) : null;
      const hasPermission = showAll || ticketUserIdNum === currentUserIdNum || !ticket.userId;
      if (!hasPermission) {
        return false;
      }
      
      // ✅ 2. VERIFICAR QUE EL TICKET PERTENECE A ESTA PESTAÑA
      // (misma lógica que create / update / limpieza, vía helper único)
      if (!ticketBelongsToTab(ticket, status)) {
        return false;
      }

      // ✅ 3. VERIFICAR FILTROS DE COLA
      // ✅ CORRECCIÓN v1.3.0: Si showTicketWithoutQueue es true, no filtrar por queueId
      const showTicketWithoutQueue = user?.allTicket === 'enabled';
      if (!showTicketWithoutQueue && selectedQueueIds && selectedQueueIds.length > 0 && ticket.queueId) {
        if (!selectedQueueIds.includes(ticket.queueId)) {
          return false;
        }
      }
      
      return true;
    };

    // const notBelongsToUserQueues = (ticket) =>
    //   ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

    // ✅ SUSCRIPCIÓN INMEDIATA SIN ESPERAR "ready"
    if (status) {
      socket.emit("joinTickets", status);
      // ✅ FASE 1: ELIMINADO - Suscripciones cruzadas que causaban bugs
      // Las pestañas solo deben escuchar SU PROPIO canal
      // El backend ya emite DELETE al canal correcto (oldStatus)
      // El backend ya emite UPDATE al canal correcto (newStatus)
    } else {
      socket.emit("joinNotification");
    }

    on("ready", () => {
      if (status) {
        socket.emit("joinTickets", status);
        // ✅ FASE 1: ELIMINADO - Suscripciones cruzadas
      } else {
        socket.emit("joinNotification");
      }
    });

    on("connect", () => {
      // ✅ REDUCIR SPAM DE LOGS - Solo log en desarrollo
      if ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')) {
        // console.log("🔄 TicketsListCustom: Socket reconectado, re-suscribiendo...");
      }
      
      // ✅ RE-SUSCRIBIRSE A LOS CANALES DESPUÉS DE RECONEXIÓN
      if (status) {
        if ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')) {
          // console.log("🔔 TicketsListCustom: Emitiendo joinTickets para status:", status);
        }
        socket.emit("joinTickets", status);
        // ✅ FASE 1: ELIMINADO - Suscripciones cruzadas que causaban bugs de reabrir tickets
      } else {
        if ((typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')) {
          // console.log("🔔 TicketsListCustom: Emitiendo joinNotification");
        }
        socket.emit("joinNotification");
      }
      
      // ✅ FASE 1: ELIMINADO - Re-registro de listeners dentro de connect
      // Los listeners ya están registrados FUERA de este callback (línea ~634)
      // Re-registrarlos aquí causaba duplicación de eventos (cada listener se ejecutaba 2 veces)
      // Solo necesitamos re-suscribirnos a canales (joinTickets) arriba ↑
    });

    on("disconnect", (reason) => {
      // console.log("⚠️ TicketsListCustom: Socket desconectado:", reason);
    });

    on(`company-${companyId}-ticket`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        if (!isMounted.current) {
          return;
        }
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento ticket inválido - data no es un objeto:', data);
          return;
        }
        
        // ✅ ACTUALIZAR REACCIONES EN LA VISTA PREVIA
        if (data.action === "update" && data.ticket && data.ticket.lastMessageWithReactions) {
          dispatch({
            type: "UPDATE_TICKET_REACTIONS",
            payload: {
              ticketId: data.ticket.id,
              lastMessageWithReactions: data.ticket.lastMessageWithReactions
            }
          });
          return; // ✅ Salir temprano para evitar procesamiento adicional
        }
      
      // ✅ LÓGICA DE FILTRADO PARA CREACIÓN DE TICKETS
      if (data.action === "create") {
        // #5: deduplicar con la referencia viva, no con la closure `ticketsList`.
        const alreadyInList = (ticketsListRef.current || []).some(t => t.id === data.ticket.id);
        const ticketUserIdNum = data.ticket.userId ? Number(data.ticket.userId) : null;
        const currentUserIdNum = user?.id ? Number(user?.id) : null;
        const hasUserPermission = showAll || ticketUserIdNum === currentUserIdNum || !data.ticket.userId;

        if (ticketBelongsToTab(data.ticket, status) && hasUserPermission && !alreadyInList) {
          dispatch({
            type: "ADD_TICKET",
            payload: data.ticket,
          });
        }
      }
      
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      // ✅ LÓGICA DE FILTRADO PARA ACTUALIZACIÓN DE TICKETS
      if (data.action === "update") {
        const existingTicket = (ticketsListRef.current || []).find(t => t.id === data.ticket.id);

        // 🚀 PASO 1: ¿El ticket DEBERÍA estar en esta pestaña? (helper único)
        const shouldBeInThisTab = ticketBelongsToTab(data.ticket, status);

        // 🚀 PASO 2: Verificar permisos de usuario (INDEPENDIENTE del status)
        // ✅ CORRECCIÓN: Verificar permisos directamente, sin usar shouldUpdateTicket
        // porque shouldUpdateTicket también verifica el status, lo cual causa falsos negativos
        const ticketUserIdNum = data.ticket.userId ? Number(data.ticket.userId) : null;
        const currentUserIdNum = user?.id ? Number(user?.id) : null;
        const hasUserPermission = showAll || ticketUserIdNum === currentUserIdNum || !data.ticket.userId;
        
        // 🚀 PASO 3: Decidir la acción final (ADD, UPDATE, DELETE o NADA)
        if (shouldBeInThisTab && hasUserPermission) {
          if (existingTicket) {
            // ✅ OPTIMIZADO: Logs solo en desarrollo
            // console.log(`[TicketsListCustom] ✅ Actualizando ticket ${data.ticket.id} en lista`);
            dispatch({
              type: "UPDATE_TICKET",
              payload: data.ticket,
            });
          } else {
            // ✅ OPTIMIZADO: Logs solo en desarrollo
            // console.log(`[TicketsListCustom] ➕ Agregando ticket ${data.ticket.id} a lista`);
            dispatch({
              type: "ADD_TICKET",
              payload: data.ticket,
            });
          }
        } else {
          // Si el ticket NO debería estar en esta pestaña (o no tiene permisos)
          // Y actualmente existe en la lista, entonces lo removemos.
          if (existingTicket) {
            // ✅ OPTIMIZADO: Logs solo en desarrollo
            // console.log(`[TicketsListCustom] 🗑️ Removiendo ticket ${data.ticket.id} de lista (no cumple filtros)`);
            dispatch({
              type: "DELETE_TICKET",
              payload: data.ticket.id,
            });
          }
        }
      }

      if (data.action === "delete") {
        // ✅ SOLO procesar DELETE si el ticket está en ESTA lista
        // Esto previene que pestañas inactivas eliminen tickets de otras pestañas
        const ticketExists = (ticketsListRef.current || []).some(t => t.id === data.ticketId);
        if (ticketExists) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
        }
      }
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener company-ticket:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    on(`company-${companyId}-appMessage`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        if (!isMounted.current) return;
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento appMessage inválido - data no es un objeto:', data);
          return;
        }
        
        const queueIds = queues.map((q) => q.id);
        if (
          !isCompanyAdmin(user) &&
          (queueIds.indexOf(data.ticket?.queue?.id) === -1 ||
            data.ticket.queue === null)
        ) {
          return;
        }

      // ✅ FIX 12/04/2025: Excluir grupos de este bloque también
      // ✅ FIX 05/12/2025: Usar optional chaining para evitar crash si data.ticket es undefined
      const isGroupTicket = data.ticket?.contact?.isGroup;
      
      // ✅ FIX 05/12/2025: Relajar filtros para permitir actualizaciones de mensajes
      // Si el mensaje es del usuario actual (fromMe), siempre actualizar
      const isMessageFromMe = data.message?.fromMe === true;
      
      // ✅ FIX 05/12/2025: Verificar si el ticket ya existe en la lista (usar some() para mejor rendimiento)
      const ticketExists = (ticketsListRef.current || []).some(t => t.id === data.ticket?.id);
      
      if (data.action === "create" && data.ticket) {
        // ✅ Si el ticket YA EXISTE en la lista, actualizar siempre (sin filtros restrictivos)
        // Esto asegura que los tickets visibles se actualicen en tiempo real
        if (ticketExists) {
        dispatch({
            type: "UPDATE_TICKET",
          payload: data.ticket,
        });
        }
        // ✅ Si el mensaje es del usuario actual, actualizar siempre (sin filtros restrictivos)
        else if (isMessageFromMe) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
        } 
        // ✅ Si no es del usuario actual y no existe en la lista, aplicar filtros normales
        else if (shouldUpdateTicket(data.ticket) && (status === undefined || data.ticket.status === status) && !isGroupTicket) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
        }
      }

      if (data.action === "update" && data.ticket) {
        // #10: no despachar UPDATE_TICKET a ciegas. El reducer haría unshift de un
        // ticket que quizá no pertenece a esta pestaña. Solo si ya está en la lista
        // o corresponde a esta pestaña.
        if (ticketExists || ticketBelongsToTab(data.ticket, status)) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
        }
      }
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener appMessage:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    on(`company-${companyId}-presence`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        // ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE DISPATCH
        if (!isMounted.current) {
          return;
        }
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento presence inválido - data no es un objeto:', data);
          return;
        }
      
      // ✅ MEJORAR TIMEOUT: Limpiar timeout anterior si existe
      if (data.presence === "composing") {
        const ticketId = data.ticketId;
        const existingTimeout = presenceTimeouts.current[ticketId];
        
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        
        // ✅ CREAR NUEVO TIMEOUT
        const newTimeout = setTimeout(() => {
          dispatch({
            type: "UPDATE_TICKET_PRESENCE",
            payload: {
              ticketId: ticketId,
              presence: "available"
            }
          });
        }, 10000); // 10 segundos
        
        // ✅ GUARDAR REFERENCIA DEL TIMEOUT
        presenceTimeouts.current[ticketId] = newTimeout;
      }
      
      dispatch({
        type: "UPDATE_TICKET_PRESENCE",
        payload: data,
      });
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener presence:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    on(`company-${companyId}-contact`, (data) => {
      // ✅ PROTECCIÓN CONTRA ERRORES SILENCIOSOS QUE BLOQUEAN LA UI
      try {
        // ✅ VERIFICAR SI EL COMPONENTE ESTÁ MONTADO ANTES DE DISPATCH
        if (!isMounted.current) return;
        
        // ✅ VALIDACIÓN: Verificar que data existe y tiene estructura válida
        if (!data || typeof data !== 'object') {
          console.warn('⚠️ TicketsListCustom: Evento contact inválido - data no es un objeto:', data);
          return;
        }
        
        if (data.action === "update") {
          dispatch({
            type: "UPDATE_TICKET_CONTACT",
            payload: data.contact,
          });
        }
      } catch (error) {
        // ✅ CAPTURAR ERRORES SILENCIOSOS QUE PODRÍAN BLOQUEAR LA UI
        console.error('❌ TicketsListCustom: Error en listener contact:', error, 'Data:', data);
        // ✅ NO PROPAGAR EL ERROR - Permitir que la aplicación continúe funcionando
      }
    });

    return () => {
      isMounted.current = false;
      // ✅ SOLO REMOVER NUESTROS PROPIOS HANDLERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      // ni tocar los listeners de otros componentes (bug #1 de la revisión).
      if (socket && typeof socket.off === 'function') {
        listeners.forEach(([event, handler]) => socket.off(event, handler));
      }
    };
  }, [status, showAll, user?.id, selectedQueueIds, tags, users, profile, queues, socketManager, excludeAIAgents]); // eslint-disable-line react-hooks/exhaustive-deps

  // NOTA: los efectos de cleanup-al-desmontar, updateCount y el intervalo de 2s
  // estaban DUPLICADOS literalmente aquí abajo. Se dejó una sola copia (arriba,
  // antes del efecto de socket) y se eliminó el intervalo (ver #11).

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // ✅ VERIFICACIONES DE SEGURIDAD DESPUÉS DE TODOS LOS HOOKS
  if (!props) {
    console.error("❌ TicketsListCustom: props es undefined");
    return null;
  }
  
  if (!user) {
    console.error("❌ TicketsListCustom: user es undefined");
    return null;
  }
  
  if (!queues || !Array.isArray(queues)) {
    console.error("❌ TicketsListCustom: queues es undefined o no es array:", queues);
    return null;
  }

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem
                  ticket={ticket}
                  status={status}
                  key={ticket.id}
                />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
